import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, CornerDownLeft, Search, type LucideIcon } from "lucide-react";
import { NAV, navHref } from "@/app/nav";
import { useAuth } from "@/app/auth";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";
import { Modal, Spinner } from "@/components/ui";

interface SearchHit {
  type: "customer" | "order" | "product" | "conversation" | "campaign" | "lead";
  id: string;
  title: string;
  subtitle?: string;
  link: string;
}

const TYPE_LABELS: Record<SearchHit["type"], string> = {
  customer: "Clients",
  order: "Commandes",
  product: "Produits",
  conversation: "Conversations",
  campaign: "Campagnes",
  lead: "Leads",
};

function useDebounced<T>(value: T, delay = 220): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const normalize = (s: string) =>
  s
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase();

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { can, me } = useAuth();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const debounced = useDebounced(q.trim());

  useEffect(() => {
    if (!open) {
      setQ("");
      setActive(0);
    }
  }, [open]);

  const search = useQuery({
    queryKey: ["search", debounced],
    queryFn: () => api.get<{ items: SearchHit[] }>("/search", { query: { q: debounced } }).catch(() => ({ items: [] })),
    enabled: open && debounced.length >= 2,
    staleTime: 10_000,
  });

  const pages = useMemo(() => {
    const items = NAV.flatMap((s) =>
      s.items
        .filter((i) => (i.platformAdminOnly ? me?.user.isPlatformAdmin : !i.permission || can(i.permission)))
        .map((i) => ({ label: i.label, to: navHref(i), icon: i.icon, section: s.title })),
    );
    if (!q.trim()) return items.slice(0, 8);
    const nq = normalize(q.trim());
    return items.filter((i) => normalize(`${i.label} ${i.section}`).includes(nq)).slice(0, 6);
  }, [q, can, me]);

  type Row = { key: string; label: string; sub?: string; to: string; group: string; icon?: LucideIcon };
  const rows: Row[] = useMemo(() => {
    const hits: Row[] = (search.data?.items ?? []).map((h) => ({ key: `${h.type}:${h.id}`, label: h.title, sub: h.subtitle, to: h.link, group: TYPE_LABELS[h.type] }));
    const pageRows: Row[] = pages.map((p) => ({ key: `page:${p.to}`, label: p.label, sub: p.section, to: p.to, group: "Pages", icon: p.icon }));
    return [...hits, ...pageRows];
  }, [search.data, pages]);

  useEffect(() => setActive(0), [rows.length]);

  const go = (row: Row | undefined) => {
    if (!row) return;
    onClose();
    navigate(row.to);
  };

  let lastGroup = "";
  return (
    <Modal open={open} onClose={onClose} size="lg" className="sm:mt-[-10vh]">
      <div className="-mx-5 -my-4">
        <div className="flex items-center gap-3 border-b border-line px-5">
          <Search className="size-5 text-muted" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActive((a) => Math.min(rows.length - 1, a + 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActive((a) => Math.max(0, a - 1));
              } else if (e.key === "Enter") {
                e.preventDefault();
                go(rows[active]);
              }
            }}
            placeholder="Rechercher des clients, produits, commandes, conversations..."
            className="h-14 w-full bg-transparent text-[15px] text-ink placeholder:text-faint focus:outline-none"
            aria-label="Recherche globale"
          />
          {search.isFetching ? <Spinner className="size-4" /> : null}
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {rows.length === 0 ? (
            <p className="px-3 py-10 text-center text-[13px] text-muted">{debounced.length >= 2 ? "Aucun résultat" : "Tapez au moins deux caractères"}</p>
          ) : (
            rows.map((row, i) => {
              const header = row.group !== lastGroup ? row.group : null;
              lastGroup = row.group;
              const Icon = row.icon ?? ArrowRight;
              return (
                <div key={row.key}>
                  {header ? <p className="px-3 pb-1 pt-3 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted">{header}</p> : null}
                  <button
                    type="button"
                    onMouseEnter={() => setActive(i)}
                    onClick={() => go(row)}
                    className={cn("flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left", i === active ? "bg-panel-3" : "hover:bg-panel-2")}
                  >
                    <Icon className="size-4 shrink-0 text-muted" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13.5px] text-ink">{row.label}</span>
                      {row.sub ? <span className="block truncate text-xs text-muted">{row.sub}</span> : null}
                    </span>
                    {i === active ? <CornerDownLeft className="size-3.5 text-muted" /> : null}
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </Modal>
  );
}
